/**
 * App — root router with auth gating and role-based access.
 *
 * Flow:
 *   1. Default entry point → /login (always)
 *   2. Login page shows "Set up BOS" link when onboarding not yet complete
 *   3. /onboarding accessible directly, no auth required; redirects to /login on completion
 *   4. Logged in as admin → full dashboard + admin routes
 *   5. Logged in as user → scoped dashboard (no system config)
 */

import React, { Suspense, lazy, useState, useCallback } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Rascals } from './pages/Rascals';
import { Layout } from './components/Layout';
import { PageLoader } from './components/LoadingSpinner';
import { WorkspaceContext, type WorkspaceType } from './hooks/useWorkspace';
import { useProduct } from './context/ProductContext';

// Lazy-load pages
const Dashboard    = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Office       = lazy(() => import('./pages/Office'));
const Login        = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const Settings     = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const ClaudeAuth   = lazy(() => import('./pages/ClaudeAuth').then((m) => ({ default: m.ClaudeAuth })));
import { HermesSetup } from './pages/HermesSetup';
const Onboarding   = lazy(() => import('./pages/Onboarding').then((m) => ({ default: m.Onboarding })));
const Calendar     = lazy(() => import('./pages/Calendar'));
const Paperclip    = lazy(() => import('./pages/Paperclip'));
const LinkedIn     = lazy(() => import('./pages/LinkedIn'));
const SignalDesk   = lazy(() => import('./pages/SignalDesk'));
const WhatsApp     = lazy(() => import('./pages/WhatsApp'));
import { GuestConfidential } from './components/guest/GuestConfidential';
const TaskBoard    = lazy(() => import('./pages/TaskBoard'));
const Canvas       = lazy(() => import('./pages/Canvas'));
const CRM          = lazy(() => import('./pages/CRM'));
const OC           = lazy(() => import('./pages/OC'));
const Board        = lazy(() => import('./pages/Board'));
const JoinMeeting  = lazy(() => import('./pages/JoinMeeting'));
const COO          = lazy(() => import('./pages/COO'));
const AgentWorkspace = lazy(() => import('./pages/AgentWorkspace').then((m) => ({ default: m.OutsiderWorkspace })));
const RascalWorkspace = lazy(() => import('./pages/AgentWorkspace').then((m) => ({ default: m.RascalWorkspace })));
const EmployeeAgents = lazy(() => import('./pages/EmployeeAgents').then((m) => ({ default: m.EmployeeAgents })));
const Health       = lazy(() => import('./pages/Health'));
const HealthHolo   = lazy(() => import('./pages/HealthHolo'));
const HealthJournal = lazy(() => import('./pages/HealthJournal'));
const HealthMedicalRecords = lazy(() => import('./pages/HealthMedicalRecords'));
// Vasari-HOS — the personal surface. Partner module to BOS, or standalone.
const HosDashboard   = lazy(() => import('./pages/hos/HosDashboard'));
const HosAssistant   = lazy(() => import('./pages/hos/HosAssistant'));
const HosSocial      = lazy(() => import('./pages/hos/HosSocial'));
const HosAddressBook = lazy(() => import('./pages/hos/HosAddressBook'));
const HosFitness     = lazy(() => import('./pages/hos/HosFitness'));
const HosNutrition   = lazy(() => import('./pages/hos/HosNutrition'));
const HosFinances    = lazy(() => import('./pages/hos/HosFinances'));
const HosSpiritual   = lazy(() => import('./pages/hos/HosSpiritual'));

function getUser(): { role: string } | null {
  try {
    const raw = localStorage.getItem('boss_user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isLoggedIn(): boolean {
  return !!localStorage.getItem('boss_token');
}

function isOnboarded(): boolean {
  return localStorage.getItem('boss_onboarding_complete') === 'true';
}

/**
 * Requires a valid login token.
 * Redirects to /login if not authenticated.
 * Onboarding check is intentionally removed — the login page
 * surfaces the onboarding link when needed.
 */
function RequireAuth() {
  const location = useLocation();
  const [workspace, setWorkspace] = useState<WorkspaceType>(null);
  const [browserUrl, setBrowserUrl] = useState<string | null>(null);

  const openWorkspace = useCallback((id: string) => {
    setWorkspace(id as WorkspaceType);
  }, []);

  const openBrowser = useCallback((url: string) => {
    setBrowserUrl(url);
    setWorkspace('browser');
  }, []);

  const closeWorkspace = useCallback(() => {
    setWorkspace(null);
    setBrowserUrl(null);
  }, []);

  if (!isLoggedIn()) return <Navigate to="/login" replace state={{ from: location }} />;
  return (
    <WorkspaceContext.Provider value={{ workspace, browserUrl, openWorkspace, openBrowser, closeWorkspace }}>
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </Layout>
    </WorkspaceContext.Provider>
  );
}

/**
 * Admin-only routes. Users see a redirect to dashboard.
 */
function RequireAdminOrViewer() {
  const user = getUser();
  if (!user || (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'viewer')) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

function RequireAdmin() {
  const user = getUser();
  if (!user || (user.role !== 'admin' && user.role !== 'owner')) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

/**
 * HOS surfaces are only reachable on an install that actually runs HOS, and
 * only for the owner — the personal surface is one person's, unlike the BOS
 * boards a team may share.
 */
/**
 * Every HOS guard must wait for the product manifest before it can judge
 * anything. The context starts on a BOS-shaped fallback with `loading: true`,
 * so redirecting during that window bounced a refresh on any /hos page straight
 * back to the BOS dashboard — the config saying "HOS exists" arrived after the
 * navigation had already happened.
 */
function RequireHos() {
  const { availableProducts, loading } = useProduct();
  const user = getUser();
  if (loading) return <PageLoader />;
  if (!availableProducts.includes('hos')) return <Navigate to="/" replace />;
  // Read-only guests (viewer) see the HOS side too; the API keeps it read-only.
  if (!user || (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'viewer')) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** A module the installer did not enable should not be routable at all. */
function RequireHosModule({ moduleId }: { moduleId: string }) {
  const { moduleEnabled, loading } = useProduct();
  if (loading) return <PageLoader />;
  if (!moduleEnabled(moduleId)) return <Navigate to="/hos" replace />;
  return <Outlet />;
}

function RequireAnyHosModule({ moduleIds }: { moduleIds: string[] }) {
  const { moduleEnabled, loading } = useProduct();
  if (loading) return <PageLoader />;
  if (!moduleIds.some(moduleEnabled)) return <Navigate to="/hos" replace />;
  return <Outlet />;
}

/**
 * WhatsApp and social belong to HOS only when HOS stands alone. Partnered with
 * BOS they are managed from the business side, and surfacing them twice would
 * have two shells fighting over one linked account.
 */
function RequireStandaloneHos() {
  const { config, loading } = useProduct();
  if (loading) return <PageLoader />;
  if (config.edition !== 'hos') return <Navigate to="/hos" replace />;
  return <Outlet />;
}

/** Health moved under the HOS namespace; old links should still land. */
function LegacyHealthRedirect({ suffix = '' }: { suffix?: string }) {
  const { availableProducts, moduleEnabled, loading } = useProduct();
  if (loading) return <PageLoader />;
  if (!availableProducts.includes('hos') || !moduleEnabled('health')) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/hos/health${suffix}`} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/join/:code" element={<JoinMeeting />} />

        {/* Authenticated routes */}
        <Route element={<RequireAuth />}>
          {/* Top-level tabs — all roles */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/office" element={<Office />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/kanban" element={<TaskBoard />} />
          <Route path="/tasks" element={<TaskBoard />} />
          <Route path="/board" element={<Board />} />
          <Route path="/canvas" element={<Canvas />} />
          <Route path="/paperclip" element={<Paperclip />} />
          <Route path="/crm" element={<CRM />} />
          <Route path="/linkedin" element={<LinkedIn />} />
          <Route path="/signal-desk" element={<SignalDesk />} />
          <Route path="/social" element={<Navigate to="/linkedin" replace />} />
          <Route path="/whatsapp" element={<GuestConfidential label="WhatsApp"><WhatsApp /></GuestConfidential>} />
          <Route path="/oc" element={<OC />} />
          <Route path="/coo" element={<COO />} />
          <Route path="/rascals" element={<Rascals />} />
          <Route path="/rascals/:handle" element={<RascalWorkspace />} />
          <Route path="/setup/claude-auth" element={<ClaudeAuth />} />
          <Route path="/setup/hermes" element={<HermesSetup />} />

          {/* Agents: admins, plus read-only guests (staff and employee agents are
              visible to them; the API refuses anything they send). */}
          <Route element={<RequireAdminOrViewer />}>
            <Route path="/agents"            element={<EmployeeAgents />} />
            <Route path="/agents/:handle"    element={<AgentWorkspace />} />
          </Route>

          {/* Admin-only system routes */}
          <Route element={<RequireAdmin />}>
            <Route path="/settings"     element={<Settings />} />
            <Route path="/settings/:section" element={<Navigate to="/settings" replace />} />
            <Route path="/admin"        element={<Navigate to="/settings" replace />} />
            <Route path="/hermes"       element={<Navigate to="/settings" replace />} />
            <Route path="/chief"        element={<Navigate to="/settings" replace />} />
            <Route path="/voice"        element={<Navigate to="/settings" replace />} />
            <Route path="/brain"        element={<Navigate to="/settings" replace />} />
            <Route path="/connectors"   element={<Navigate to="/settings" replace />} />
            <Route path="/learning"     element={<Navigate to="/settings" replace />} />
            <Route path="/self-healing" element={<Navigate to="/settings" replace />} />
            <Route path="/backup"       element={<Navigate to="/settings" replace />} />
          </Route>

          {/* Vasari-HOS — personal surfaces in their own namespace, sharing the
              shell. Health, Journal and Records keep their original pages and
              simply move under /hos. */}
          <Route element={<RequireHos />}>
            <Route path="/hos" element={<HosDashboard />} />
            <Route path="/hos/assistant" element={<HosAssistant />} />
            <Route element={<RequireHosModule moduleId="health" />}>
              <Route path="/hos/health" element={<Health />} />
              <Route path="/hos/health/holo" element={<HealthHolo />} />
              <Route path="/hos/health/journal" element={<HealthJournal />} />
              <Route path="/hos/health/records" element={<HealthMedicalRecords />} />
            </Route>
            <Route element={<RequireHosModule moduleId="fitness" />}>
              <Route path="/hos/fitness" element={<HosFitness />} />
            </Route>
            <Route element={<RequireHosModule moduleId="nutrition" />}>
              <Route path="/hos/nutrition" element={<HosNutrition />} />
            </Route>
            <Route element={<RequireHosModule moduleId="finances" />}>
              <Route path="/hos/finances" element={<HosFinances />} />
            </Route>
            <Route element={<RequireHosModule moduleId="spiritual" />}>
              <Route path="/hos/spiritual" element={<HosSpiritual />} />
            </Route>
            <Route element={<RequireHosModule moduleId="address-book" />}>
              <Route path="/hos/address-book" element={<HosAddressBook />} />
            </Route>
            {/* Standalone-only: partnered installs manage these from BOS. */}
            <Route element={<RequireStandaloneHos />}>
              <Route element={<RequireHosModule moduleId="social" />}>
                <Route path="/hos/social" element={<HosSocial />} />
              </Route>
              <Route element={<RequireAnyHosModule moduleIds={['social', 'address-book']} />}>
                <Route path="/hos/whatsapp" element={<GuestConfidential label="WhatsApp"><WhatsApp /></GuestConfidential>} />
              </Route>
            </Route>
          </Route>

          {/* Health used to live at /health on the BOS rail. */}
          <Route path="/health" element={<LegacyHealthRedirect />} />
          <Route path="/health/holo" element={<LegacyHealthRedirect suffix="/holo" />} />
          <Route path="/health/journal" element={<LegacyHealthRedirect suffix="/journal" />} />
          <Route path="/health/records" element={<LegacyHealthRedirect suffix="/records" />} />
        </Route>

        {/* Catch-all → login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}
